import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { publicPhotoUpload } from "../api";
import BancoLogo from "../assets/Logo.png";

const MESSAGES = {
    invalid_name: "El nombre no puede estar vacío.",
    name_too_long: "El nombre es demasiado largo.",
};

const errorMessage = (e) => {
    if (e?.status === 404) return "Este enlace no es válido.";
    if (e?.status === 410) return "La sesión terminó o venció. Pedí un nuevo código en la computadora.";
    if (e?.status === 429) return "Demasiados intentos, esperá un momento.";
    return MESSAGES[e?.code] || e?.detail || "No se pudo completar la acción.";
};

export default function MobileUploadPage() {
    const { token } = useParams();
    const [info, setInfo] = useState();
    const [photos, setPhotos] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState();
    const inputRef = useRef();

    useEffect(() => {
        publicPhotoUpload.get(token).then(setInfo).catch((e) => setError(errorMessage(e)));
    }, [token]);

    const onFiles = async (event) => {
        const files = Array.from(event.target.files);
        event.target.value = "";
        setBusy(true);
        setError(undefined);
        // Sequential: the back counts photos under a row lock, parallel adds nothing.
        for (const file of files) {
            try {
                const doc = await publicPhotoUpload.upload(token, file);
                setPhotos((prev) => [...prev, { ...doc, draft: doc.name }]);
                setInfo((prev) => ({ ...prev, uploadedCount: doc.uploadedCount }));
            } catch (e) {
                setError(errorMessage(e));
                break;
            }
        }
        setBusy(false);
    };

    const rename = async (photo) => {
        if (photo.draft === photo.name) return;
        try {
            const saved = await publicPhotoUpload.rename(token, photo.id, photo.draft);
            setPhotos((prev) =>
                prev.map((p) => (p.id === photo.id ? { ...p, name: saved.name, draft: saved.name } : p))
            );
            setError(undefined);
        } catch (e) {
            setError(errorMessage(e));
        }
    };

    const full = info && info.uploadedCount >= info.maxPhotos;
    const inactive = info && info.status !== "active";
    const loading = !info && !error;

    return (
        <div className="min-h-screen bg-white">
            <div className="mx-auto max-w-md px-4 pb-8">
                <header className="border-b border-gray-200 py-4">
                    <img src={BancoLogo} alt="Banco de Ojos" className="h-9" />
                </header>

                {loading && <p className="mt-8 text-base text-gray-600">Cargando...</p>}

                {info && (
                    <section className="mt-6">
                        <p className="text-sm text-gray-600">Subiendo fotos de</p>
                        <h1 className="text-3xl font-bold leading-9 text-gray-900">{info.patientDisplayName}</h1>
                        <div
                            className="mt-4 h-1 bg-gray-200"
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={info.maxPhotos}
                            aria-valuenow={info.uploadedCount}
                        >
                            <div
                                className="h-1 bg-primary"
                                style={{ width: `${Math.min(100, (info.uploadedCount / info.maxPhotos) * 100)}%` }}
                            />
                        </div>
                        <p className="mt-2 text-sm text-gray-600">
                            {info.uploadedCount} de {info.maxPhotos} fotos
                        </p>
                    </section>
                )}

                {error && (
                    <p role="alert" className="mt-6 text-base font-medium text-red-700">
                        {error}
                    </p>
                )}

                {inactive && (
                    <p role="alert" className="mt-6 text-base font-medium text-red-700">
                        La sesión ya no está activa. Pedí un nuevo código en la computadora.
                    </p>
                )}

                {info && !inactive && (
                    <>
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={onFiles}
                        />
                        <button
                            type="button"
                            disabled={busy || full}
                            onClick={() => inputRef.current.click()}
                            className="mt-6 min-h-[48px] w-full rounded-md bg-primary px-4 text-base font-semibold text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:opacity-50"
                        >
                            {busy ? "Subiendo..." : full ? "Límite alcanzado" : "Tomar o elegir fotos"}
                        </button>
                    </>
                )}

                {info && !inactive && (
                    <section className="mt-8">
                        <h2 className="text-lg font-semibold text-gray-900">Fotos de esta sesión</h2>
                        {photos.length === 0 ? (
                            <p className="mt-2 text-sm text-gray-600">Todavía no subiste ninguna foto.</p>
                        ) : (
                            <ul className="mt-2 divide-y divide-gray-200 border-y border-gray-200">
                                {photos.map((p) => (
                                    <li key={p.id} className="py-3">
                                        <label className="block text-xs text-gray-600" htmlFor={`photo-${p.id}`}>
                                            Nombre
                                        </label>
                                        <input
                                            id={`photo-${p.id}`}
                                            className="mt-1 block min-h-[44px] w-full rounded-md border-gray-400 text-base"
                                            value={p.draft}
                                            onChange={(e) =>
                                                setPhotos((prev) =>
                                                    prev.map((x) => (x.id === p.id ? { ...x, draft: e.target.value } : x))
                                                )
                                            }
                                            onBlur={() => rename(p)}
                                        />
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                )}
            </div>
        </div>
    );
}
